/**
 * TemplatePreviewModal Component
 * Phase 2 Sprint 14: Template Preview & Selection
 * 
 * Displays detailed template preview with scenes and scripts
 */

import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Tag, Popover } from 'antd';
import { 
  PlayCircle, 
  Clock,
  FileText,
  Info,
  Briefcase,
  Trash2,
  Layout,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Sparkles,
  Sliders,
  Play,
  ArrowLeft
} from 'lucide-react';
import { Template } from '../../../../types/template';
import { TEMPLATE_CATEGORIES } from '../../../../data/templateSeeds';
import { 
  landscapePresetsWithPreview, 
  portraitPresetsWithPreview, 
  squarePresetsWithPreview,
  LayoutPresetWithPreview
} from '../../presets/layoutPresetsPreview';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { UpgradePrompt } from '../overlays/UpgradePrompt';
import './TemplatePreviewModal.scss';

interface TemplatePreviewModalProps {
  template: Template | null;
  visible: boolean;
  onClose: () => void;
  onUseTemplate: (template: Template) => void;
  onMakeItYours?: (template: Template) => void;
  onCustomizeTemplate: (template: Template) => void;
  onDeleteTemplate?: (template: Template) => void;
}

export const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  template,
  visible,
  onClose,
  onUseTemplate,
  onMakeItYours,
  onCustomizeTemplate,
  onDeleteTemplate
}) => {
  const [expandedScenes, setExpandedScenes] = useState<Set<number>>(new Set());
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { isPaid } = useFeatureAccess();

  useEffect(() => {
    if (!visible) {
      setIsDemoMode(false);
    }
  }, [visible]);

  useEffect(() => {
    if (isDemoMode && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [isDemoMode]);

  if (!template) return null;

  const getDemoVideoPath = (templateId: string): string | null => {
    const demoMap: Record<string, string> = {
      'biz-pitch-deck': '/templates/demos/pitch-deck.mp4',
      'biz-product-demo': '/templates/demos/product-demo.mp4',
      'biz-company-culture': '/templates/demos/company-culture.mp4',
      'content-social-promo': '/templates/demos/social-promo.mp4',
      'content-youtube-opener': '/templates/demos/youtube-opener.mp4',
      'dev-code-tutorial': '/templates/demos/code-tutorial.mp4',
      'dev-api-docs': '/templates/demos/api-docs.mp4',
      'edu-course-intro': '/templates/demos/course-intro.mp4',
      'edu-student-testimonial': '/templates/demos/student-testimonial.mp4',
      'edu-tutorial': '/templates/demos/tutorial-explainer.mp4',
      'interview-intro': '/templates/demos/candidate-intro.mp4',
      'interview-remote-setup': '/templates/demos/remote-setup.mp4',
    };
    return demoMap[templateId] || null;
  };

  const demoVideoPath = getDemoVideoPath(template.id);
  const hasDemo = !!demoVideoPath;

  const toggleSceneExpanded = (sceneOrder: number) => {
    setExpandedScenes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sceneOrder)) {
        newSet.delete(sceneOrder);
      } else {
        newSet.add(sceneOrder);
      }
      return newSet;
    });
  };

  const totalDuration = template.scenes.reduce(
    (sum, scene) => sum + scene.suggestedDuration,
    0
  );
  const minutes = Math.floor(totalDuration / 60);
  const seconds = totalDuration % 60;

  const getCategoryLabel = () => {
    return TEMPLATE_CATEGORIES[template.category]?.label || template.category;
  };

  const getLayoutPresetInfo = (presetKey: string): LayoutPresetWithPreview | undefined => {
    // Select preset array based on template aspect ratio
    let presetsToSearch: LayoutPresetWithPreview[];
    if (template.aspectRatio === '9:16') {
      presetsToSearch = [...portraitPresetsWithPreview, ...landscapePresetsWithPreview, ...squarePresetsWithPreview];
    } else if (template.aspectRatio === '1:1') {
      presetsToSearch = [...squarePresetsWithPreview, ...landscapePresetsWithPreview, ...portraitPresetsWithPreview];
    } else {
      presetsToSearch = [...landscapePresetsWithPreview, ...portraitPresetsWithPreview, ...squarePresetsWithPreview];
    }
    return presetsToSearch.find(p => p.key === presetKey);
  };

  const renderLayoutWithPopover = (presetKey: string) => {
    const presetInfo = getLayoutPresetInfo(presetKey);
    
    if (!presetInfo) {
      return <span className="layout-name">{presetKey}</span>;
    }

    const popoverContent = (
      <div className="layout-preview-popover">
        <div className="layout-preview-image">
          <img src={presetInfo.preview} alt={presetInfo.name} />
        </div>
        <span className="layout-preview-name">{presetInfo.name}</span>
      </div>
    );

    return (
      <Popover 
        content={popoverContent} 
        placement="top"
        overlayClassName="layout-preview-popover-overlay"
      >
        <span className="layout-name clickable">{presetInfo.name}</span>
      </Popover>
    );
  };

  const getTemplateThumbnail = (templateId: string): string | null => {
    const thumbnailMap: Record<string, string> = {
      'edu-course-intro': '/templates/course-intro.jpeg',
      'edu-tutorial': '/templates/tutorial-explainer.jpeg',
      'edu-student-testimonial': '/templates/student-testimonial.jpeg',
      'biz-pitch-deck': '/templates/pitch-deck.jpeg',
      'biz-product-demo': '/templates/product-demo.jpeg',
      'biz-company-culture': '/templates/company-culture.jpeg',
      'content-youtube-opener': '/templates/youtube-opener.jpeg',
      'content-social-promo': '/templates/social-promo.jpeg',
      'dev-code-tutorial': '/templates/code-tutorial.jpeg',
      'dev-api-docs': '/templates/api-doc.jpeg',
      'interview-intro': '/templates/candidate-intro.jpeg',
      'interview-remote-setup': '/templates/remote-interview.jpeg',
    };
    return thumbnailMap[templateId] || null;
  };

  const thumbnail = getTemplateThumbnail(template.id) || '/templates/custom.jpeg';

  const renderSceneCards = () => {
    return (
      <div className="scene-cards">
        {template.scenes.map((scene, index) => {
          const sceneMinutes = Math.floor(scene.suggestedDuration / 60);
          const sceneSeconds = scene.suggestedDuration % 60;
          const script = template.scripts.find(s => s.key === scene.scriptKey);
          const isExpanded = expandedScenes.has(scene.order);
          
          return (
            <div key={scene.order} className={`scene-card ${isExpanded ? 'expanded' : ''}`}>
              <div 
                className="scene-card-header"
                onClick={() => script && toggleSceneExpanded(scene.order)}
                style={{ cursor: script ? 'pointer' : 'default' }}
              >
                <span className="scene-number">{index + 1}</span>
                <div className="scene-info">
                  <h4 className="scene-title">{scene.title}</h4>
                  <div className="scene-meta">
                    <span className="meta-item">
                      <Clock size={12} />
                      {sceneMinutes}:{sceneSeconds.toString().padStart(2, '0')}
                    </span>
                    <span className="meta-item layout-meta">
                      <Layout size={12} />
                      {renderLayoutWithPopover(scene.layoutPreset)}
                    </span>
                    {script && (
                      <span className="meta-item script-indicator">
                        <FileText size={12} />
                        Script
                      </span>
                    )}
                  </div>
                </div>
                {script && (
                  <button className="expand-toggle">
                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                )}
              </div>
              
              {scene.tags && scene.tags.length > 0 && (
                <div className="scene-tags">
                  {scene.tags.map(tag => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </div>
              )}

              {scene.notes && (
                <div className="scene-notes">
                  <Info size={14} />
                  <span>{scene.notes}</span>
                </div>
              )}

              {script && !isExpanded && (
                <div 
                  className="scene-script-preview"
                  onClick={() => toggleSceneExpanded(scene.order)}
                >
                  <FileText size={14} />
                  <span>
                    {script.content.slice(0, 100)}
                    {script.content.length > 100 ? '...' : ''}
                  </span>
                </div>
              )}

              {script && isExpanded && (
                <div className="scene-script-full">
                  <div className="script-header">
                    <FileText size={16} />
                    <span>Script</span>
                  </div>
                  <p className="script-content">{script.content}</p>
                  
                  {script.defaultExample && (
                    <div className="script-example">
                      <Lightbulb size={14} />
                      <div>
                        <strong>Example</strong>
                        <p>{script.defaultExample}</p>
                      </div>
                    </div>
                  )}
                  
                  {script.customizationNote && (
                    <div className="script-tip">
                      <Info size={14} />
                      <span>{script.customizationNote}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      width={isDemoMode ? 800 : 960}
      className={`template-preview-modal ${isDemoMode ? 'demo-mode' : ''}`}
      footer={null}
    >
      <div className={`template-preview-layout ${isDemoMode ? 'demo-mode-layout' : ''}`}>
        {/* Left Column - Thumbnail & Quick Stats (or Video Player in demo mode) */}
        <div className={`preview-left ${isDemoMode ? 'preview-left--fullwidth' : ''}`}>
          {isDemoMode ? (
            <div className="demo-video-container">
              <button 
                className="demo-back-button"
                onClick={() => setIsDemoMode(false)}
              >
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              <div className="demo-video-wrapper">
                <video
                  ref={videoRef}
                  src={demoVideoPath || ''}
                  controls
                  autoPlay
                  playsInline
                  className="demo-video-player"
                />
              </div>
              <div className="demo-video-info">
                <h3>{template.title}</h3>
                <p>See how this template looks in action</p>
              </div>
            </div>
          ) : (
            <>
              <div className="thumbnail-container">
                <img src={thumbnail} alt={template.title} />
                <div className="thumbnail-overlay">
                  {template.isOfficial ? (
                    <span className="badge official">Official</span>
                  ) : (
                    <span className="badge custom">Custom</span>
                  )}
                </div>
              </div>

              {hasDemo && (
                <Button
                  className="play-demo-button"
                  icon={<Play size={16} />}
                  onClick={() => setIsDemoMode(true)}
                  block
                >
                  Watch Demo
                </Button>
              )}

              <div className="quick-stats">
            <div className="stat-item">
              <span className="stat-label">Scenes</span>
              <span className="stat-value">{template.scenes.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Duration</span>
              <span className="stat-value">{minutes}:{seconds.toString().padStart(2, '0')}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Scripts</span>
              <span className="stat-value">{template.scripts.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Aspect Ratio</span>
              <span className="stat-value">{template.aspectRatio || '16:9'}</span>
            </div>
          </div>

          <div className="action-buttons">
            <Button 
              type="primary" 
              icon={<PlayCircle size={18} />}
              onClick={() => onUseTemplate(template)}
              block
              size="large"
              className="cta-primary"
            >
              Use Template
            </Button>
            {onMakeItYours && template.isOfficial && (
              <Button 
                type="default"
                icon={<Sparkles size={16} />}
                onClick={() => {
                  if (!isPaid) {
                    setShowUpgradePrompt(true);
                    return;
                  }
                  onMakeItYours(template);
                }}
                block
                className="cta-secondary"
              >
                <span className="button-content">
                  <span className="button-label">Make It Your Own</span>
                  <span className="button-subtitle">AI-guided personalization</span>
                </span>
              </Button>
            )}
            <Button 
              type="text"
              icon={<Sliders size={16} />}
              onClick={() => onCustomizeTemplate(template)}
              block
              className="cta-tertiary"
            >
              Advanced Customization
            </Button>
            {!template.isOfficial && onDeleteTemplate && (
              <Button 
                type="text"
                danger
                icon={<Trash2 size={16} />}
                onClick={() => onDeleteTemplate(template)}
                block
                className="cta-delete"
              >
                Delete Template
              </Button>
            )}
          </div>
            </>
          )}
        </div>

        {/* Right Column - Details (hidden in demo mode) */}
        <div className={`preview-right ${isDemoMode ? 'preview-right--hidden' : ''}`}>
          <div className="header-section">
            <div className="category-badge">
              <Briefcase size={14} />
              <span>{getCategoryLabel()}</span>
            </div>
            <h2 className="template-title">{template.title}</h2>
            <p className="template-description">{template.description}</p>
          </div>

          <div className="output-section">
            <div className="section-header">
              <span className="section-title">What You'll Create</span>
            </div>
            <div className="output-card">
              <h4>{template.exampleOutput.title}</h4>
              <p>{template.exampleOutput.whatUserGets}</p>
              <Tag color="purple">
                ~{Math.floor(template.exampleOutput.duration / 60)} min video
              </Tag>
            </div>
          </div>

          <div className="scenes-section">
            <div className="section-header">
              <span className="section-title">Scenes & Scripts</span>
              <span className="section-count">{template.scenes.length} scenes</span>
            </div>
            <p className="section-hint">Click on a scene to expand its full script</p>
            {renderSceneCards()}
          </div>
        </div>
      </div>
      
      <UpgradePrompt
        visible={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        feature="AI-Guided Personalization"
        description="Make It Your Own uses AI to personalize templates based on your needs. Upgrade to Pro to unlock this feature and create custom templates tailored to your content."
      />
    </Modal>
  );
};

export default TemplatePreviewModal;
